import { Box, Icon as ChakraIcon, Text } from '@chakra-ui/react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { FiLogOut } from 'react-icons/fi';
import { LuBot, LuFileKey2, LuLayoutDashboard, LuLayoutPanelTop, LuMessageSquareMore, LuUsersRound } from 'react-icons/lu';
import { MdOutlineAnalytics } from 'react-icons/md';

import SidebarItem from '@/components/ui/SidebarItem/SidebarItem';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';

const SIDEBAR_WIDTH_EXPANDED = 220;
const SIDEBAR_WIDTH_COLLAPSED = 80;
const SIDEBAR_EXPAND_TRANSITION_DELAY_MS = 250;

const MENU_ITEMS = [
  {
    id: 1,
    Icon: LuLayoutDashboard,
    label: 'Dashboard',
    href: ROUTES.DASHBOARD,
  },
  {
    id: 2,
    Icon: LuFileKey2,
    label: 'Credentials',
    href: ROUTES.CREDENTIALS,
  },
  {
    id: 3,
    Icon: LuBot,
    label: 'Agents',
    href: ROUTES.AGENTS,
  },
  {
    id: 4,
    Icon: LuUsersRound,
    label: 'Customers',
    href: ROUTES.CUSTOMER_MANAGEMENT,
  },
  {
    id: 5,
    Icon: LuMessageSquareMore,
    label: 'Chatbots',
    href: ROUTES.CHATS,
  },
  {
    id: 6,
    Icon: LuLayoutPanelTop,
    label: 'Control Panel',
    href: ROUTES.CONTROL_PANEL,
  },
  {
    id: 7,
    Icon: MdOutlineAnalytics,
    label: 'Analytics',
    href: ROUTES.ANALYTICS,
  },
  // {
  //   id: 4,
  //   Icon: LuFolderOpen,
  //   href: '/settings',
  // },
];

const Sidebar = () => {
  const pathname = usePathname();
  const [activeFlagPosition, setActiveFlagPosition] = useState(0);
  const [activeItem, setActiveItem] = useState<HTMLElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { handleLogout } = useAuth();

  useEffect(() => {
    const updateActiveItem = () => {
      const activeItemElement = document?.getElementById(`${pathname}-sidebar-item`);
      if (activeItemElement) {
        setActiveItem(activeItemElement);
        setActiveFlagPosition(activeItemElement.offsetTop);
      }
    };

    updateActiveItem();

    const timer = setTimeout(
      updateActiveItem,
      SIDEBAR_EXPAND_TRANSITION_DELAY_MS,
    );

    return () => clearTimeout(timer);
  }, [pathname, isExpanded]);

  return (
    <Box
      position="relative"
      width={isExpanded ? `${SIDEBAR_WIDTH_EXPANDED}px` : `${SIDEBAR_WIDTH_COLLAPSED}px`}
      minWidth={isExpanded ? `${SIDEBAR_WIDTH_EXPANDED}px` : `${SIDEBAR_WIDTH_COLLAPSED}px`}
      maxWidth={isExpanded ? `${SIDEBAR_WIDTH_EXPANDED}px` : `${SIDEBAR_WIDTH_COLLAPSED}px`}
      height="100%"
      py={3}
      display="flex"
      flexDirection="column"
      alignItems="center"
      borderRight={1}
      borderStyle="solid"
      borderColor="#E0E0E0"
      backgroundColor="white"
      gap={3}
      transition="width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      overflow="hidden"
      flexShrink={0}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={isExpanded ? 2 : 0}
        minWidth={0}
        width="100%"
        justifyContent="center"
        px={2}
        minHeight="42px"
      >
        <Image
          src="/logo.svg"
          alt="Logo"
          priority
          width={isExpanded ? 20 : 40}
          height={isExpanded ? 20 : 40}
          style={{ transition: 'width 0.2s ease, height 0.2s ease' }}
        />
        {isExpanded && (
          <Text
            color="#410479"
            fontFamily="var(--font-jura), Jura, sans-serif"
            fontSize="24px"
            fontStyle="normal"
            fontWeight={600}
            lineHeight="normal"
            whiteSpace="nowrap"
            overflow="hidden"
          >
            AgentDesk
          </Text>
        )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        minWidth={0}
        alignItems="stretch"
        gap={1}
        position="relative"
        flex={1}
      >
        {MENU_ITEMS.map((item) => {
          const { Icon, href, id, label } = item;

          const isActive = pathname === href;

          return (
            <SidebarItem
              key={id}
              href={href}
              isActive={isActive}
              Icon={Icon}
              label={label}
              isExpanded={isExpanded}
            />
          );
        })}
        <Box
          position="absolute"
          height={`${activeItem?.offsetHeight ?? 0}px`}
          top={`${activeFlagPosition}px`}
          transition="top 0.2s ease"
          left={0}
          width="3px"
          borderTopRightRadius={10}
          borderBottomRightRadius={10}
          backgroundColor="purple.500"
          zIndex={1}
          pointerEvents="none"
        />
        <Box
          width="80%"
          height="1px"
          backgroundColor="#E0E0E0"
          mx="auto"
          mt={4}
          mb={1}
        />
        <Box
          width="100%"
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          pl={7}
          pr={isExpanded ? 4 : 7}
          gap={3}
          py={4}
          cursor="pointer"
          backgroundColor="transparent"
          borderRadius={0}
          fontFamily="inherit"
          onClick={() => handleLogout(true)}
          aria-label="Log out"
          _hover={{
            backgroundColor: 'purple.50',
          }}
        >
          <ChakraIcon as={FiLogOut} color="purple.800" width="24px" height="24px" flexShrink={0} />
          <Text
            fontSize="sm"
            fontWeight={500}
            color="purple.800"
            whiteSpace="nowrap"
            overflow="hidden"
            visibility={isExpanded ? 'visible' : 'hidden'}
            maxW={isExpanded ? 'none' : 0}
          >
            Log out
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
